import { html, render, nothing, type TemplateResult } from 'lit-html';
import listStyles from './pk-report-event-list.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shape of one event row passed via the `events` property.
 * The `raw` field carries the full original event payload for the detail drawer.
 */
export interface PkEventRecord {
    id:        string;
    timestamp: string;
    actor?:    string;
    type?:     string;
    summary?:  string;
    severity?: string;
    raw?:      Record<string, unknown>;
    [key: string]: unknown;
}

/** Column-type discriminator — drives built-in cell renderers. */
export type PkEventColumnType = 'text' | 'datetime' | 'severity-chip';

/**
 * Column descriptor accepted by the `columns` property (v0.8 CMS-driven API).
 * A bare string is shorthand for `{ key }` with a humanized label.
 *
 * `key` is looked up VERBATIM against each row object — for `actor.upn` the
 * component reads `row["actor.upn"]` (a flat property whose name contains a
 * dot), it does NOT traverse a nested path.
 */
export interface PkEventColumnSpec {
    key:     string;
    label?:  string;
    align?:  'left' | 'center' | 'right';
    format?: PkEventColumnType;
}

/** Sort direction state. 'none' means the column is not the active sort key. */
type SortDir = 'asc' | 'desc' | 'none';

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-event-column>
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-event-column>` — declarative column descriptor for
 * `<pk-report-event-list>`. Place as a child element; the parent reads its
 * attributes via MutationObserver.
 *
 * Attributes:
 *   `key`           — property name on each event record (required).
 *   `label`         — column header text (required).
 *   `type`          — `text` (default) | `datetime` | `severity-chip`.
 *   `sortable`      — boolean; opt the column into client-side sort.
 *   `default-sort`  — `asc` | `desc`; sets the initial sort state for this
 *                     column. Only one column should have this at a time.
 *   `width`         — CSS width value (e.g. `"160px"`, `"auto"`). Default `auto`.
 */
export class PkReportEventColumn extends HTMLElement {
    static readonly tagName = 'pk-report-event-column';
}

if (!customElements.get(PkReportEventColumn.tagName)) {
    customElements.define(PkReportEventColumn.tagName, PkReportEventColumn);
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal column descriptor (parsed from <pk-report-event-column>)
// ─────────────────────────────────────────────────────────────────────────────

interface ColDef {
    key:         string;
    label:       string;
    type:        PkEventColumnType;
    sortable:    boolean;
    defaultSort: 'asc' | 'desc' | null;
    width:       string;
    align:       'left' | 'center' | 'right';
}

/**
 * Humanize a (possibly dotted) column key into a Title Case label.
 * e.g. `actor.upn` → "Actor Upn", `timestamp` → "Timestamp",
 * `tokens_total` → "Tokens Total".
 */
function humanizeKey(key: string): string {
    return key
        .split(/[.\s_-]+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format an ISO date string to `YYYY-MM-DD HH:mm:ss` UTC.
 * Returns the original string as a fallback if parsing fails.
 */
function formatDatetime(iso: string): string {
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        const pad = (n: number, w = 2) => String(n).padStart(w, '0');
        return [
            d.getUTCFullYear(), '-',
            pad(d.getUTCMonth() + 1), '-',
            pad(d.getUTCDate()),      ' ',
            pad(d.getUTCHours()),     ':',
            pad(d.getUTCMinutes()),   ':',
            pad(d.getUTCSeconds()),
        ].join('');
    } catch {
        return iso;
    }
}

/** Map a severity string to its CSS custom property. */
function severityColor(s: string): string {
    switch (s?.toLowerCase()) {
        case 'error':   return 'var(--pk-color-danger,  #a23131)';
        case 'warning':
        case 'warn':    return 'var(--pk-color-warn,    #c37d10)';
        case 'info':    return 'var(--pk-color-info,    #1a5eb8)';
        case 'debug':
        default:        return 'var(--pk-text-muted,    rgba(24,27,34,0.55))';
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-event-list>
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-event-list>` — sortable, paginated / virtualized event-detail
 * table with row-click flyout pattern. v0.7
 *
 * Designed for high event counts (10k+ rows in a 30-day window). When
 * `events.length >= virtualization-threshold`, the component switches from
 * simple page-based slicing to a virtual-scroll approach that only renders the
 * rows visible in the scroll container plus a configurable buffer (no external
 * library — plain visible-window math with a `scroll` listener).
 *
 * Data is set **imperatively** via the `events` property (not via DOM slotting,
 * which would be unusably slow at 10k rows).
 *
 * Column layout is declared via `<pk-report-event-column>` child elements:
 *
 * ```html
 * <pk-report-event-list
 *   empty-message="No events in the selected time range."
 *   page-size="50"
 *   virtualization-threshold="200">
 *   <pk-report-event-column key="timestamp" label="Time"     type="datetime"       sortable default-sort="desc" width="160px"></pk-report-event-column>
 *   <pk-report-event-column key="actor"     label="Actor"    sortable width="200px"></pk-report-event-column>
 *   <pk-report-event-column key="type"      label="Type"     sortable width="120px"></pk-report-event-column>
 *   <pk-report-event-column key="summary"   label="Summary"  width="auto"></pk-report-event-column>
 *   <pk-report-event-column key="severity"  label="Severity" type="severity-chip"  sortable width="100px"></pk-report-event-column>
 * </pk-report-event-list>
 * ```
 *
 * Then set data imperatively:
 * ```js
 * list.events = [...];
 * ```
 *
 * Attributes:
 *   `empty-message`            — text shown when `events` is empty (default: "No events.").
 *   `page-size`                — rows per page in paginated mode (default: 50).
 *   `virtualization-threshold` — row count at which pagination → virtual scroll (default: 200).
 *
 * Events:
 *   `pk-event-select` — `CustomEvent<{ event: PkEventRecord }>` fired on row
 *                       click or Enter key. The consumer is responsible for
 *                       opening a `<pk-side-sheet>` to show the detail.
 *
 * Keyboard:
 *   - Arrow Up / Down navigate between rows.
 *   - Enter on a focused row fires `pk-event-select`.
 *   - Tab cycles through column headers.
 *
 * ARIA:
 *   `role="table"` on the outer table; `role="columnheader"` + `aria-sort` on
 *   headers; `role="row"` on rows.
 */
export class PkReportEventList extends HTMLElement {
    static readonly tagName = 'pk-report-event-list';

    static get observedAttributes(): string[] {
        return ['empty-message', 'page-size', 'virtualization-threshold', 'columns'];
    }

    private readonly _root: ShadowRoot;
    private readonly _colObserver: MutationObserver;

    // Data
    private _events: PkEventRecord[] = [];

    // Column definitions (resolved each render — property > attribute > children)
    private _cols: ColDef[] = [];

    // Columns supplied via the JS `columns` property (takes precedence over both
    // the `columns` attribute and child <pk-report-event-column> elements).
    private _columnsProp: PkEventColumnSpec[] | null = null;

    // Sort state
    private _sortKey:  string  = '';
    private _sortDir:  SortDir = 'none';

    // Pagination state (used in non-virtual mode)
    private _page: number = 0;

    // Virtual scroll state
    private _scrollTop: number = 0;
    private _viewportHeight: number = 400; // estimated; updated on first render

    // Row height estimate for virtual scroll (px). Actual DOM rows vary, but a
    // conservative 40px keeps the buffer safe.
    private static readonly ROW_HEIGHT = 40;
    // Buffer rows to render above and below the visible window.
    private static readonly V_BUFFER   = 10;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });

        // Watch for <pk-report-event-column> children being added/removed/changed.
        this._colObserver = new MutationObserver(() => {
            this._readCols();
            this._render();
        });
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    connectedCallback(): void {
        this._upgradeProperty('columns');
        this._upgradeProperty('events');
        this._upgradeProperty('rows');
        this._colObserver.observe(this, { childList: true, subtree: true, attributes: true });
        this._readCols();
        this._applyDefaultSort();
        this._render();
    }

    disconnectedCallback(): void {
        this._colObserver.disconnect();
    }

    attributeChangedCallback(name: string): void {
        if (name === 'columns') {
            this._readCols();
            this._applyDefaultSort();
        }
        if (this.isConnected) this._render();
    }

    /**
     * Property-upgrade pattern: if a value was assigned to an own property
     * before the element was defined/upgraded, re-run it through the accessor.
     */
    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    // ── Public API ───────────────────────────────────────────────────────────

    /** Set the event rows programmatically. Resets page/scroll to top. */
    set events(rows: PkEventRecord[]) {
        this._events = Array.isArray(rows) ? rows : [];
        this._page = 0;
        this._scrollTop = 0;
        this._render();
    }

    get events(): PkEventRecord[] {
        return this._events;
    }

    /** `rows` is a v0.8 compat alias for `events` (CMS-driven imperative API). */
    set rows(v: PkEventRecord[]) {
        this.events = v;
    }

    get rows(): PkEventRecord[] {
        return this.events;
    }

    /**
     * Declare columns imperatively (v0.8 CMS-driven API). Accepts either an
     * array of string keys or an array of `{ key, label?, align?, format? }`
     * specs. Takes precedence over the `columns` attribute and over child
     * `<pk-report-event-column>` elements. Pass `null` to fall back to those.
     */
    set columns(value: (string | PkEventColumnSpec)[] | null | undefined) {
        if (value == null) {
            this._columnsProp = null;
        } else if (Array.isArray(value)) {
            this._columnsProp = value.map(c =>
                typeof c === 'string' ? { key: c } : { ...c });
        } else {
            this._columnsProp = null;
        }
        this._readCols();
        this._applyDefaultSort();
        this._render();
    }

    get columns(): PkEventColumnSpec[] | null {
        return this._columnsProp;
    }

    // ── Column reading ───────────────────────────────────────────────────────

    /**
     * Resolve the active column set. Precedence:
     *   1. the `columns` JS property (array of keys or specs),
     *   2. the `columns` attribute (comma-separated keys),
     *   3. child `<pk-report-event-column>` elements (legacy / backward-compat).
     */
    private _readCols(): void {
        if (this._columnsProp != null) {
            this._cols = this._columnsProp.map(spec => this._specToCol(spec));
            return;
        }

        const attr = this.getAttribute('columns');
        if (attr != null && attr.trim() !== '') {
            this._cols = attr
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map(key => this._specToCol({ key }));
            return;
        }

        const els = Array.from(this.querySelectorAll('pk-report-event-column'));
        this._cols = els.map(el => {
            const typeAttr = el.getAttribute('type') ?? 'text';
            const type: PkEventColumnType =
                typeAttr === 'datetime' || typeAttr === 'severity-chip' ? typeAttr : 'text';
            const ds = el.getAttribute('default-sort');
            const alignAttr = el.getAttribute('align');
            const align: ColDef['align'] =
                alignAttr === 'center' || alignAttr === 'right' ? alignAttr : 'left';
            return {
                key:         el.getAttribute('key')   ?? '',
                label:       el.getAttribute('label') ?? '',
                type,
                sortable:    el.hasAttribute('sortable'),
                defaultSort: ds === 'asc' || ds === 'desc' ? ds : null,
                width:       el.getAttribute('width') ?? 'auto',
                align,
            };
        });
    }

    /** Convert a `columns`-property/attribute spec into an internal ColDef. */
    private _specToCol(spec: PkEventColumnSpec): ColDef {
        const fmt = spec.format;
        const type: PkEventColumnType =
            fmt === 'datetime' || fmt === 'severity-chip' ? fmt : 'text';
        return {
            key:         spec.key,
            label:       spec.label != null ? spec.label : humanizeKey(spec.key),
            type,
            // Property/attribute-declared columns are sortable by default so the
            // CMS `sortable` host attribute behaves as expected.
            sortable:    true,
            defaultSort: null,
            width:       'auto',
            align:       spec.align === 'center' || spec.align === 'right' ? spec.align : 'left',
        };
    }

    private _applyDefaultSort(): void {
        for (const col of this._cols) {
            if (col.defaultSort) {
                this._sortKey = col.key;
                this._sortDir = col.defaultSort;
                return;
            }
        }
    }

    // ── Derived state ────────────────────────────────────────────────────────

    private _pageSize(): number {
        const v = Number(this.getAttribute('page-size') ?? '50');
        return Number.isFinite(v) && v > 0 ? Math.floor(v) : 50;
    }

    private _vThreshold(): number {
        const v = Number(this.getAttribute('virtualization-threshold') ?? '200');
        return Number.isFinite(v) && v > 0 ? Math.floor(v) : 200;
    }

    private _isVirtual(): boolean {
        return this._events.length >= this._vThreshold();
    }

    private _sortedEvents(): PkEventRecord[] {
        const rows = [...this._events];
        const key  = this._sortKey;
        if (!key || this._sortDir === 'none') return rows;
        const dir = this._sortDir === 'desc' ? -1 : 1;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            // Numeric compare if both parse; otherwise localeCompare.
            const an = Number(av);
            const bn = Number(bv);
            if (Number.isFinite(an) && Number.isFinite(bn)) return (an < bn ? -1 : 1) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
        return rows;
    }

    // ── Event dispatch ───────────────────────────────────────────────────────

    private _selectEvent(evt: PkEventRecord): void {
        this.dispatchEvent(new CustomEvent('pk-event-select', {
            bubbles:  true,
            composed: true,
            detail:   { event: evt },
        }));
    }

    // ── Interaction handlers ─────────────────────────────────────────────────

    private readonly _onHeaderClick = (col: ColDef): void => {
        if (!col.sortable) return;
        if (this._sortKey === col.key) {
            // Cycle: asc → desc → none → asc …
            this._sortDir = this._sortDir === 'asc' ? 'desc' : this._sortDir === 'desc' ? 'none' : 'asc';
            if (this._sortDir === 'none') this._sortKey = '';
        } else {
            this._sortKey = col.key;
            this._sortDir = 'asc';
        }
        this._page = 0;
        this._render();
    };

    private readonly _onRowClick = (evt: PkEventRecord): void => {
        this._selectEvent(evt);
    };

    private readonly _onRowKeyDown = (e: KeyboardEvent, evt: PkEventRecord, idx: number): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._selectEvent(evt);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            this._focusRow(idx + 1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this._focusRow(idx - 1);
        }
    };

    private _focusRow(idx: number): void {
        const rows = Array.from(this._root.querySelectorAll<HTMLElement>('[role="row"][tabindex]'));
        // Skip the header row (idx 0 in querySelectorAll is the header)
        const dataRows = rows.filter(r => r.dataset.rowidx !== undefined);
        const target = dataRows[idx];
        if (target) target.focus();
    }

    private readonly _onPagePrev = (): void => {
        if (this._page > 0) { this._page--; this._render(); }
    };

    private readonly _onPageNext = (pages: number): void => {
        if (this._page < pages - 1) { this._page++; this._render(); }
    };

    private readonly _onScrollContainer = (e: Event): void => {
        const el = e.currentTarget as HTMLElement;
        this._scrollTop = el.scrollTop;
        this._viewportHeight = el.clientHeight || 400;
        this._render();
    };

    // ── Cell renderers ───────────────────────────────────────────────────────

    private _cellTpl(row: PkEventRecord, col: ColDef): TemplateResult {
        const raw = row[col.key];
        const str = raw == null ? '' : String(raw);

        if (col.type === 'datetime') {
            const display = str ? formatDatetime(str) : '—';
            return html`<span class="cell-datetime" title=${str}>${display}</span>`;
        }

        if (col.type === 'severity-chip') {
            const color = severityColor(str);
            return html`<span class="severity-chip" style="--sev-color:${color}">${str || '—'}</span>`;
        }

        return html`${str || '—'}`;
    }

    // ── Header row template ──────────────────────────────────────────────────

    private _headerRowTpl(): TemplateResult {
        return html`
            <div class="thead" role="row">
                ${this._cols.map(col => {
                    const isActive = this._sortKey === col.key && this._sortDir !== 'none';
                    const ariaSortAttr = isActive
                        ? (this._sortDir === 'asc' ? 'ascending' : 'descending')
                        : (col.sortable ? 'none' : nothing);
                    const arrow = isActive
                        ? (this._sortDir === 'asc' ? ' ▲' : ' ▼')
                        : '';
                    return html`
                        <div
                            role="columnheader"
                            class="th ${col.sortable ? 'sortable' : ''} ${isActive ? 'sorted' : ''}"
                            style="--col-w:${col.width};text-align:${col.align}"
                            aria-sort=${ariaSortAttr}
                            tabindex=${col.sortable ? '0' : '-1'}
                            @click=${() => this._onHeaderClick(col)}
                            @keydown=${(e: KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    this._onHeaderClick(col);
                                }
                            }}
                        >${col.label}${arrow}</div>
                    `;
                })}
            </div>
        `;
    }

    // ── Data row template ────────────────────────────────────────────────────

    private _dataRowTpl(row: PkEventRecord, logicalIdx: number): TemplateResult {
        return html`
            <div
                role="row"
                class="tr"
                tabindex="0"
                data-rowidx=${logicalIdx}
                @click=${() => this._onRowClick(row)}
                @keydown=${(e: KeyboardEvent) => this._onRowKeyDown(e, row, logicalIdx)}
            >
                ${this._cols.map(col => html`
                    <div role="cell" class="td" style="--col-w:${col.width};text-align:${col.align}">
                        ${this._cellTpl(row, col)}
                    </div>
                `)}
            </div>
        `;
    }

    // ── Pagination template ──────────────────────────────────────────────────

    private _pagerTpl(total: number, page: number, pages: number, pageSize: number): TemplateResult {
        const start = total === 0 ? 0 : page * pageSize + 1;
        const end   = Math.min(total, (page + 1) * pageSize);
        return html`
            <div class="pager" role="navigation" aria-label="Page navigation">
                <span class="pager-info">${start}–${end} of ${total}</span>
                <button type="button" class="pager-btn"
                    ?disabled=${page === 0}
                    @click=${this._onPagePrev}
                    aria-label="Previous page">‹ Prev</button>
                <button type="button" class="pager-btn"
                    ?disabled=${page >= pages - 1}
                    @click=${() => this._onPageNext(pages)}
                    aria-label="Next page">Next ›</button>
            </div>
        `;
    }

    // ── Main template ────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const sorted       = this._sortedEvents();
        const emptyMessage = this.getAttribute('empty-message') ?? 'No events.';
        const isEmpty      = sorted.length === 0;
        const isVirtual    = this._isVirtual();

        // ── Empty state ──────────────────────────────────────────────────────
        if (isEmpty) {
            return html`
                <style>${sharedTokens}${listStyles}</style>
                <div class="event-list-panel">
                    ${this._headerRowTpl()}
                    <div class="empty-state-wrap">
                        <p class="empty-message">${emptyMessage}</p>
                        <slot name="empty-state"></slot>
                    </div>
                </div>
            `;
        }

        // ── Virtual scroll mode ──────────────────────────────────────────────
        if (isVirtual) {
            const rowH   = PkReportEventList.ROW_HEIGHT;
            const buf    = PkReportEventList.V_BUFFER;
            const total  = sorted.length;
            const vpH    = this._viewportHeight;
            const scroll = this._scrollTop;

            const firstVisible = Math.max(0, Math.floor(scroll / rowH) - buf);
            const lastVisible  = Math.min(total - 1, Math.ceil((scroll + vpH) / rowH) + buf);
            const visibleRows  = sorted.slice(firstVisible, lastVisible + 1);

            const spacerTop    = firstVisible * rowH;
            const spacerBottom = (total - lastVisible - 1) * rowH;

            return html`
                <style>${sharedTokens}${listStyles}</style>
                <div class="event-list-panel">
                    ${this._headerRowTpl()}
                    <div class="scroll-container virtual"
                         role="table"
                         aria-label="Event list (${total} events, virtual scroll)"
                         aria-rowcount=${total}
                         @scroll=${this._onScrollContainer}>
                        <div style="height:${spacerTop}px" aria-hidden="true"></div>
                        ${visibleRows.map((row, i) =>
                            this._dataRowTpl(row, firstVisible + i)
                        )}
                        <div style="height:${spacerBottom}px" aria-hidden="true"></div>
                    </div>
                    <div class="virtual-count-bar">
                        ${total.toLocaleString()} events — scroll to browse
                    </div>
                </div>
            `;
        }

        // ── Paginated mode ───────────────────────────────────────────────────
        const pageSize = this._pageSize();
        const total    = sorted.length;
        const pages    = Math.max(1, Math.ceil(total / pageSize));
        const page     = Math.min(this._page, pages - 1);
        const start    = page * pageSize;
        const visible  = sorted.slice(start, start + pageSize);

        return html`
            <style>${sharedTokens}${listStyles}</style>
            <div class="event-list-panel">
                <div role="table" aria-label="Event list" aria-rowcount=${total}>
                    ${this._headerRowTpl()}
                    <div class="tbody" role="rowgroup">
                        ${visible.map((row, i) => this._dataRowTpl(row, start + i))}
                    </div>
                </div>
                ${this._pagerTpl(total, page, pages, pageSize)}
            </div>
        `;
    }

    private _render(): void {
        render(this._template(), this._root);

        // After first virtual render, capture the actual viewport height from the
        // scroll container so subsequent renders use real measurements.
        if (this._isVirtual()) {
            const sc = this._root.querySelector<HTMLElement>('.scroll-container');
            if (sc && sc.clientHeight > 0) {
                this._viewportHeight = sc.clientHeight;
            }
        }
    }
}

if (!customElements.get(PkReportEventList.tagName)) {
    customElements.define(PkReportEventList.tagName, PkReportEventList);
}

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-event-summary-drawer> — optional helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-event-summary-drawer>` — convenience wrapper around
 * `<pk-side-sheet>` that renders a generic event detail: timestamp, actor,
 * type, severity, and a JSON pretty-print of the `raw` payload.
 *
 * Consumers that need a custom layout can ignore this and wire their own
 * `<pk-side-sheet>` to the `pk-event-select` event instead.
 *
 * Usage:
 * ```html
 * <pk-report-event-summary-drawer id="drawer"></pk-report-event-summary-drawer>
 * ```
 * ```js
 * list.addEventListener('pk-event-select', e => {
 *   drawer.show(e.detail.event);
 * });
 * ```
 *
 * The component requires `<pk-side-sheet>` to be registered (it is, since
 * portal-kit registers all elements on import).
 */
export class PkReportEventSummaryDrawer extends HTMLElement {
    static readonly tagName = 'pk-report-event-summary-drawer';

    private readonly _root: ShadowRoot;
    private _event: PkEventRecord | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._doRender();
    }

    /** Open the drawer and display the given event. */
    show(event: PkEventRecord): void {
        this._event = event;
        this._doRender();
        const sheet = this._root.querySelector('pk-side-sheet');
        if (sheet) {
            (sheet as HTMLElement & { show(): void }).show();
        }
    }

    /** Close the drawer. */
    hide(): void {
        const sheet = this._root.querySelector('pk-side-sheet');
        if (sheet) {
            (sheet as HTMLElement & { hide(): void }).hide();
        }
    }

    private _drawerTitle(): string {
        if (!this._event) return 'Event detail';
        return this._event.type ? String(this._event.type) : 'Event detail';
    }

    private _doRender(): void {
        const evt = this._event;
        const rawJson = evt?.raw
            ? JSON.stringify(evt.raw, null, 2)
            : evt
                ? JSON.stringify(
                    Object.fromEntries(
                        Object.entries(evt).filter(([k]) => k !== 'id')
                    ),
                    null, 2
                  )
                : '';

        render(html`
            <pk-side-sheet
                title=${this._drawerTitle()}
                width="wide">
                <div slot="body" class="drawer-body">
                    ${evt ? html`
                        <dl class="event-meta">
                            ${evt.timestamp ? html`
                                <dt>Time</dt>
                                <dd title=${evt.timestamp}>${formatDatetime(evt.timestamp)}</dd>
                            ` : nothing}
                            ${evt.actor ? html`
                                <dt>Actor</dt>
                                <dd>${evt.actor}</dd>
                            ` : nothing}
                            ${evt.type ? html`
                                <dt>Type</dt>
                                <dd>${evt.type}</dd>
                            ` : nothing}
                            ${evt.severity ? html`
                                <dt>Severity</dt>
                                <dd>
                                    <span class="severity-chip"
                                          style="--sev-color:${severityColor(String(evt.severity))}">
                                        ${evt.severity}
                                    </span>
                                </dd>
                            ` : nothing}
                            ${evt.summary ? html`
                                <dt>Summary</dt>
                                <dd>${evt.summary}</dd>
                            ` : nothing}
                        </dl>
                        <details class="raw-section" open>
                            <summary class="raw-summary">Raw payload</summary>
                            <pre class="raw-json"><code>${rawJson}</code></pre>
                        </details>
                    ` : html`<p class="no-event">No event selected.</p>`}
                </div>
            </pk-side-sheet>
            <style>
                :host { display: block; }
                .drawer-body { display: flex; flex-direction: column; gap: 1rem; }
                .event-meta { display: grid; grid-template-columns: max-content 1fr; gap: 0.25rem 0.75rem; margin: 0; font-size: 0.875rem; }
                .event-meta dt { color: var(--pk-color-text-muted, rgba(24,27,34,0.65)); font-weight: 600; text-align: right; }
                .event-meta dd { margin: 0; color: var(--pk-color-text, #181B22); word-break: break-all; }
                .raw-section { margin-top: 0.5rem; }
                .raw-summary { cursor: pointer; font-size: 0.8rem; font-weight: 600; color: var(--pk-color-text-muted, rgba(24,27,34,0.65)); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.5rem; }
                .raw-json { background: var(--pk-color-bg-sunken, #FBFAF5); border: 1px solid var(--pk-color-border, #ECE8DA); border-radius: 6px; padding: 0.75rem; font-size: 0.75rem; overflow: auto; max-height: 50vh; white-space: pre; margin: 0; }
                .severity-chip { display: inline-block; padding: 0.15em 0.55em; border-radius: 99px; font-size: 0.78rem; font-weight: 600; color: #fff; background: var(--sev-color); }
                .no-event { color: var(--pk-color-text-muted, rgba(24,27,34,0.65)); font-size: 0.875rem; }
            </style>
        `, this._root);
    }
}

if (!customElements.get(PkReportEventSummaryDrawer.tagName)) {
    customElements.define(PkReportEventSummaryDrawer.tagName, PkReportEventSummaryDrawer);
}

// ─────────────────────────────────────────────────────────────────────────────
// Global augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-event-list':           PkReportEventList;
        'pk-report-event-column':         PkReportEventColumn;
        'pk-report-event-summary-drawer': PkReportEventSummaryDrawer;
    }
}
